import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiDownload, FiCalendar, FiFilter, FiFileText } from 'react-icons/fi';
import { reportesService, clientesService, productosService } from '../../services';
import useReportExport from '../../hooks/useReportExport';
import Autocomplete from '../../components/UI/Autocomplete';
import './ReportesCommon.scss';

const ReportesVentas = () => {
  const navigate = useNavigate();
  const { exportToPDF, exportToCSV } = useReportExport();
  const [activeTab, setActiveTab] = useState('general'); // general, por-producto, por-cliente
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);

  // Filtros
  const [period, setPeriod] = useState('month'); // week, month, quarter, year
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedClient, setSelectedClient] = useState('');
  const [selectedProduct, setSelectedProduct] = useState('');

  useEffect(() => {
    loadReportData();
  }, [activeTab, period, startDate, endDate, selectedClient, selectedProduct]);

  // Función de búsqueda de clientes para Autocomplete
  const searchClientes = async (searchTerm) => {
    try {
      const response = await clientesService.getClientes({
        is_active: true,
        search: searchTerm,
        limit: 50
      });
      if (response.success) {
        return response.data || [];
      }
      return [];
    } catch (error) {
      console.error('Error al buscar clientes:', error);
      return [];
    }
  };

  // Función de búsqueda de productos para Autocomplete
  const searchProductos = async (searchTerm) => {
    try {
      const response = await productosService.getProductos({
        status: 'ACTIVE',
        search: searchTerm,
        limit: 50
      });
      if (response.success) {
        return response.data || [];
      }
      return [];
    } catch (error) {
      console.error('Error al buscar productos:', error);
      return [];
    }
  };

  const loadReportData = async () => {
    setLoading(true);
    try {
      const params = {
        period,
        start_date: startDate || undefined,
        end_date: endDate || undefined
      };

      let response;

      if (activeTab === 'general') {
        response = await reportesService.getVentas(params);
      } else if (activeTab === 'por-producto' && selectedProduct) {
        response = await reportesService.getVentasPorProducto(selectedProduct, params);
      } else if (activeTab === 'por-cliente' && selectedClient) {
        response = await reportesService.getVentasPorCliente(selectedClient, params);
      } else {
        // Si no hay selección en tabs específicos, usar reporte general
        response = await reportesService.getVentas(params);
      }

      console.log('Respuesta del reporte de ventas:', response);

      if (response.success) {
        console.log('Datos del reporte:', response.data);
        // La respuesta ya viene formateada por formatApiResponse
        // response.data contiene: { report_type, summary, data: [...facturas] }
        setReportData(response.data);
      } else {
        console.error('Error en la respuesta:', response);
        setReportData(null);
      }
    } catch (error) {
      console.error('Error al cargar reporte de ventas:', error);
      setReportData(null);
    } finally {
      setLoading(false);
    }
  };

  const handleExportPDF = () => {
    const tabLabel = tabs.find(t => t.id === activeTab)?.label || 'General';
    exportToPDF(`Reporte de Ventas - ${tabLabel}`);
  };

  const tabs = [
    { id: 'general', label: 'General' },
    { id: 'por-producto', label: 'Por Producto' },
    { id: 'por-cliente', label: 'Por Cliente' }
  ];

  const periodOptions = [
    { value: 'week', label: 'Semana Actual' },
    { value: 'month', label: 'Mes Actual' },
    { value: 'quarter', label: 'Trimestre Actual' },
    { value: 'year', label: 'Año Actual' }
  ];

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(value || 0);
  };

  const formatPercentage = (value) => {
    return `${(value || 0).toFixed(2)}%`;
  };

  return (
    <div className="reportes-detail-page">
      {/* Header */}
      <div className="reportes-detail-header">
        <div className="header-top">
          <button className="btn-back" onClick={() => navigate('/system/reportes')}>
            <FiArrowLeft />
            <span>Regresar a Reportes</span>
          </button>
          <h1 className="reportes-detail-title">Reporte de Ventas</h1>
        </div>

        <div className="header-actions">
          <button className="btn-export" onClick={handleExportPDF}>
            <FiFileText />
            <span>Exportar PDF</span>
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="reportes-tabs">
        {tabs.map(tab => (
          <button
            key={tab.id}
            className={`tab-button ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Filtros */}
      <div className="reportes-filters">
        <div className="filter-group">
          <label className="filter-label">
            <FiCalendar />
            <span>Período</span>
          </label>
          <select
            className="filter-select"
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
          >
            {periodOptions.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label className="filter-label">Fecha Desde</label>
          <input
            type="date"
            className="filter-input"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </div>

        <div className="filter-group">
          <label className="filter-label">Fecha Hasta</label>
          <input
            type="date"
            className="filter-input"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </div>

        {activeTab === 'por-cliente' && (
          <div className="filter-group">
            <label className="filter-label">
              <FiFilter />
              <span>Cliente</span>
            </label>
            <Autocomplete
              value={selectedClient}
              onChange={setSelectedClient}
              onSearch={searchClientes}
              getOptionLabel={(cliente) => cliente.company_name || cliente.full_name || `Cliente #${cliente.id}`}
              getOptionValue={(cliente) => cliente.id}
              placeholder="Buscar cliente..."
              noResultsText="No se encontraron clientes"
            />
          </div>
        )}

        {activeTab === 'por-producto' && (
          <div className="filter-group">
            <label className="filter-label">
              <FiFilter />
              <span>Producto</span>
            </label>
            <Autocomplete
              value={selectedProduct}
              onChange={setSelectedProduct}
              onSearch={searchProductos}
              getOptionLabel={(producto) => `${producto.code} - ${producto.name}`}
              getOptionValue={(producto) => producto.id}
              placeholder="Buscar producto..."
              noResultsText="No se encontraron productos"
            />
          </div>
        )}
      </div>

      {/* Resumen */}
      {reportData && !Array.isArray(reportData) && reportData.summary && (
        <div className="reportes-summary">
          <div className="summary-card">
            <span className="summary-label">Total Facturas</span>
            <span className="summary-value">{reportData.summary.total_invoices || 0}</span>
          </div>
          <div className="summary-card highlight">
            <span className="summary-label">Total Ventas</span>
            <span className="summary-value">{formatCurrency(reportData.summary.total_revenue)}</span>
          </div>
          <div className="summary-card success">
            <span className="summary-label">Total Cobrado</span>
            <span className="summary-value">{formatCurrency(reportData.summary.total_paid)}</span>
          </div>
          <div className="summary-card discount">
            <span className="summary-label">Total Pendiente</span>
            <span className="summary-value">{formatCurrency(reportData.summary.total_pending)}</span>
          </div>
          <div className="summary-card">
            <span className="summary-label">Ticket Promedio</span>
            <span className="summary-value">{formatCurrency(reportData.summary.average_deal_size)}</span>
          </div>
        </div>
      )}

      {/* Resumen cuando reportData es un array */}
      {reportData && Array.isArray(reportData) && reportData.length > 0 && (
        <div className="reportes-summary">
          <div className="summary-card">
            <span className="summary-label">Total Facturas</span>
            <span className="summary-value">{reportData.length}</span>
          </div>
          <div className="summary-card highlight">
            <span className="summary-label">Total Ventas</span>
            <span className="summary-value">
              {formatCurrency(reportData.reduce((sum, inv) => sum + (inv.total || 0), 0))}
            </span>
          </div>
          <div className="summary-card success">
            <span className="summary-label">Total Cobrado</span>
            <span className="summary-value">
              {formatCurrency(reportData.reduce((sum, inv) => sum + (inv.paid_amount || 0), 0))}
            </span>
          </div>
          <div className="summary-card discount">
            <span className="summary-label">Total Pendiente</span>
            <span className="summary-value">
              {formatCurrency(reportData.reduce((sum, inv) => sum + (inv.balance || 0), 0))}
            </span>
          </div>
          <div className="summary-card">
            <span className="summary-label">Ticket Promedio</span>
            <span className="summary-value">
              {formatCurrency(reportData.reduce((sum, inv) => sum + (inv.total || 0), 0) / reportData.length)}
            </span>
          </div>
        </div>
      )}

      {/* Tabla de Facturas */}
      <div className="reportes-table-container">
        {loading ? (
          <div className="loading-state">Cargando reporte...</div>
        ) : (Array.isArray(reportData) && reportData.length > 0) || (reportData?.data && reportData.data.length > 0) ? (
          <>
          {console.log('Renderizando tabla con', Array.isArray(reportData) ? reportData.length : reportData.data.length, 'facturas')}
          <table className="reportes-table">
            <thead>
              <tr>
                <th>Fecha</th>
                <th>Factura</th>
                <th>Cliente</th>
                <th>Vendedor</th>
                <th className="text-right">Subtotal</th>
                <th className="text-right">Descuentos</th>
                <th className="text-right">Impuestos</th>
                <th className="text-right">Total</th>
                <th className="text-right">Cobrado</th>
                <th className="text-right">Saldo</th>
                <th className="text-center">Estado</th>
              </tr>
            </thead>
            <tbody>
              {(Array.isArray(reportData) ? reportData : reportData.data).map((invoice) => (
                <tr key={invoice.invoice_id}>
                  <td className="period-cell">{new Date(invoice.invoice_date).toLocaleDateString('es-MX')}</td>
                  <td className="font-bold">
                    <span className="invoice-badge">{invoice.invoice_series}-{invoice.invoice_number}</span>
                  </td>
                  <td>{invoice.client_name}</td>
                  <td>{invoice.user_name}</td>
                  <td className="text-right">{formatCurrency(invoice.subtotal)}</td>
                  <td className="text-right discount">{formatCurrency(invoice.discount_amount)}</td>
                  <td className="text-right">{formatCurrency(invoice.tax_amount)}</td>
                  <td className="text-right font-bold">{formatCurrency(invoice.total)}</td>
                  <td className="text-right success">{formatCurrency(invoice.paid_amount)}</td>
                  <td className="text-right">{formatCurrency(invoice.balance)}</td>
                  <td className="text-center">
                    <span className={`credit-status credit-status--${
                      (invoice.payment_status || '').toLowerCase() === 'paid' ? 'paid' :
                      (invoice.payment_status || '').toLowerCase() === 'pending' || (invoice.payment_status || '').toLowerCase() === 'unpaid' ? 'pending' :
                      (invoice.payment_status || '').toLowerCase() === 'partial' ? 'pending' :
                      'overdue'
                    }`}>
                      {(() => {
                        const status = (invoice.payment_status || '').toLowerCase();
                        if (status === 'paid') return 'Pagado';
                        if (status === 'unpaid') return 'No Pagado';
                        if (status === 'pending') return 'Pendiente';
                        if (status === 'partial') return 'Parcial';
                        if (status === 'overdue') return 'Vencido';
                        return invoice.payment_status;
                      })()}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </>
        ) : (
          <div className="empty-state">
            {console.log('Estado vacío - reportData:', reportData)}
            <p>No hay ventas registradas para el período seleccionado.</p>
            {reportData && !reportData.data && (
              <p style={{ fontSize: '12px', color: '#999' }}>
                Debug: reportData existe pero no tiene propiedad 'data'
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ReportesVentas;
